import { useEffect, useState } from "react";
import {
  BookOpen, CheckCircle2, ChevronRight, Clock, FileText,
  GraduationCap, Users,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";

// ─── Datos de demo ────────────────────────────────────────────────────────────

type VersionRuta = {
  version: number;
  nombre: string;
  instructor: string;
  fecha: string;
  bloques: number;
  modulos: number;
  candidatos: number;
  actual?: boolean;
};

type EstandarRutas = {
  codigo: string;
  nombre: string;
  sector: string;
  versiones: VersionRuta[];
};

const RUTAS_DEMO: EstandarRutas[] = [
  {
    codigo: "EC1781",
    nombre: "Conducción de alto desempeño de motocicletas para elementos de seguridad pública y fuerzas especiales",
    sector: "Seguridad pública",
    versiones: [
      {
        version: 3,
        nombre: "Ruta formativa EC1781 — Revisión integral 2026",
        instructor: "Rodrigo Fuentes",
        fecha: "2026-05-28",
        bloques: 7,
        modulos: 14,
        candidatos: 6,
        actual: true,
      },
      {
        version: 2,
        nombre: "Ruta formativa EC1781 — Ciclo primavera 2026",
        instructor: "Rodrigo Fuentes",
        fecha: "2026-03-12",
        bloques: 6,
        modulos: 11,
        candidatos: 5,
      },
      {
        version: 1,
        nombre: "Ruta formativa EC1781 — Versión inicial",
        instructor: "Rodrigo Fuentes",
        fecha: "2026-01-08",
        bloques: 5,
        modulos: 9,
        candidatos: 5,
      },
    ],
  },
  {
    codigo: "EC0217",
    nombre: "Impartición de cursos de formación del capital humano de manera presencial grupal",
    sector: "Educación y formación",
    versiones: [
      {
        version: 2,
        nombre: "Ruta formativa EC0217 — Actualización metodológica",
        instructor: "Ma. Luisa Vargas",
        fecha: "2026-04-15",
        bloques: 4,
        modulos: 9,
        candidatos: 4,
        actual: true,
      },
      {
        version: 1,
        nombre: "Ruta formativa EC0217 — Versión base",
        instructor: "Carlos Mendoza",
        fecha: "2026-02-01",
        bloques: 4,
        modulos: 7,
        candidatos: 3,
      },
    ],
  },
  {
    codigo: "EC1100",
    nombre: "Supervisión de servicios de seguridad privada en instalaciones",
    sector: "Seguridad privada",
    versiones: [
      {
        version: 1,
        nombre: "Ruta formativa EC1100 — Versión inicial",
        instructor: "Jorge Palomino",
        fecha: "2026-05-10",
        bloques: 3,
        modulos: 6,
        candidatos: 8,
        actual: true,
      },
    ],
  },
];

// ─── Utilidades ───────────────────────────────────────────────────────────────

function formatFecha(iso: string) {
  const [y, m, d] = iso.split("-");
  const meses = ["", "ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${d} ${meses[Number(m)]} ${y}`;
}

// ─── Tarjeta de estándar (selector izquierdo) ─────────────────────────────────

function EstandarCard({
  est,
  selected,
  onClick,
}: { est: EstandarRutas; selected: boolean; onClick: () => void }) {
  const ultima = est.versiones[0];
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%", textAlign: "left", cursor: "pointer",
        padding: "14px 16px", borderRadius: 10,
        border: `1px solid ${selected ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
        background: selected ? "rgba(8,145,178,0.05)" : "white",
        display: "flex", flexDirection: "column", gap: 6,
        transition: "all 0.15s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
          background: selected ? "var(--cc-teal)" : "var(--cc-gray-100)",
          color: selected ? "white" : "var(--cc-gray-600)",
          letterSpacing: "0.04em",
        }}>{est.codigo}</span>
        <span style={{ fontSize: 11, color: "var(--cc-gray-500)", marginLeft: "auto" }}>
          {est.versiones.length} versión{est.versiones.length !== 1 ? "es" : ""}
        </span>
        <ChevronRight size={14} style={{ color: selected ? "var(--cc-teal)" : "var(--cc-gray-400)", flexShrink: 0 }} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)", lineHeight: 1.35 }}>
        {est.nombre}
      </div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>
        Última publicación: <strong>{formatFecha(ultima.fecha)}</strong> · {ultima.instructor}
      </div>
    </button>
  );
}

// ─── Fila de versión ──────────────────────────────────────────────────────────

function VersionRow({ v, isLast }: { v: VersionRuta; isLast: boolean }) {
  return (
    <div style={{ display: "flex", gap: 14, position: "relative" }}>
      {/* Timeline line */}
      {!isLast && (
        <div style={{
          position: "absolute", left: 15, top: 32, bottom: -16,
          width: 1, background: "var(--cc-gray-100)",
        }} />
      )}

      {/* Dot */}
      <div style={{
        width: 30, height: 30, borderRadius: "50%", flexShrink: 0, marginTop: 2,
        background: v.actual ? "var(--cc-teal)" : "white",
        border: `2px solid ${v.actual ? "var(--cc-teal)" : "var(--cc-gray-200)"}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1,
      }}>
        {v.actual
          ? <CheckCircle2 size={14} color="white" />
          : <span style={{ fontSize: 10, fontWeight: 700, color: "var(--cc-gray-400)" }}>v{v.version}</span>
        }
      </div>

      {/* Content */}
      <div style={{
        flex: 1, padding: "12px 16px", borderRadius: 10,
        border: `1px solid ${v.actual ? "rgba(8,145,178,0.25)" : "var(--cc-gray-100)"}`,
        background: v.actual ? "rgba(8,145,178,0.03)" : "#fafafa",
        marginBottom: 16,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              {v.actual && (
                <Badge tone="teal">Vigente</Badge>
              )}
              <span style={{ fontSize: 12, color: "var(--cc-gray-500)", fontWeight: 500 }}>v{v.version}</span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>
              {v.nombre}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--cc-gray-600)" }}>
              <GraduationCap size={13} />
              <span><strong>{v.instructor}</strong></span>
              <span style={{ color: "var(--cc-gray-300)" }}>·</span>
              <Clock size={12} />
              <span>Publicada el {formatFecha(v.fecha)}</span>
            </div>
          </div>

          <div style={{ display: "flex", gap: 16, flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--cc-navy)" }}>{v.bloques}</div>
              <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>Bloques</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--cc-navy)" }}>{v.modulos}</div>
              <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>Módulos</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: "var(--cc-navy)" }}>{v.candidatos}</div>
              <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>Candidatos</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export default function RutasFormativasAdmin() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("admin"), [setRole]);

  const [selectedCodigo, setSelectedCodigo] = useState<string>("EC1781");
  const selected = RUTAS_DEMO.find((e) => e.codigo === selectedCodigo) ?? RUTAS_DEMO[0];

  return (
    <AppLayout pageTitle="Rutas formativas" breadcrumb={["Administrador", "Rutas formativas"]}>
      <div style={{ display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, alignItems: "start" }}>

        {/* ── Columna izquierda: selector de estándares ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--cc-gray-500)", marginBottom: 2 }}>
            Estándares
          </div>
          {RUTAS_DEMO.map((est) => (
            <EstandarCard
              key={est.codigo}
              est={est}
              selected={selectedCodigo === est.codigo}
              onClick={() => setSelectedCodigo(est.codigo)}
            />
          ))}
        </div>

        {/* ── Columna derecha: historial de versiones ── */}
        <Card>
          {/* Header del estándar */}
          <div style={{ marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{
                fontSize: 12, fontWeight: 800, padding: "3px 10px", borderRadius: 20,
                background: "var(--cc-teal)", color: "white", letterSpacing: "0.04em",
              }}>{selected.codigo}</span>
              <span style={{ fontSize: 11, color: "var(--cc-gray-400)", fontWeight: 500 }}>{selected.sector}</span>
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--cc-navy)", lineHeight: 1.3 }}>
              {selected.nombre}
            </div>
          </div>

          {/* Métricas rápidas */}
          <div style={{
            display: "flex", gap: 24, padding: "12px 16px", borderRadius: 10,
            background: "var(--cc-gray-50)", border: "1px solid var(--cc-gray-100)",
            marginBottom: 24, flexWrap: "wrap",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--cc-gray-600)" }}>
              <FileText size={14} />
              <span><strong>{selected.versiones.length}</strong> versión{selected.versiones.length !== 1 ? "es" : ""} publicadas</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--cc-gray-600)" }}>
              <Users size={14} />
              <span><strong>{selected.versiones[0].candidatos}</strong> candidatos en la versión vigente</span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--cc-gray-600)" }}>
              <BookOpen size={14} />
              <span><strong>{selected.versiones[0].modulos}</strong> módulos activos</span>
            </div>
          </div>

          {/* Timeline de versiones */}
          <SectionTitle>Historial de publicaciones</SectionTitle>
          <div style={{ paddingTop: 12 }}>
            {selected.versiones.map((v, i) => (
              <VersionRow key={v.version} v={v} isLast={i === selected.versiones.length - 1} />
            ))}
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
